import { FC, Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DynamicEventResponse } from 'ts/dynamic/interfaces/DynamicResponse';
import { TransferParams } from 'ts/dynamic/interfaces/DynamicParams';
import useRecaptcha from 'hooks/useRecaptcha';
import { changeTransfer, upsellTransfer } from 'services/dynamic';
import TransferCard from './TransferCard';
import Button from 'components/ui/Button/Button';
import { DynamicContext } from 'contexts/DynamicContext';
import withErrorBoundary from 'components/shared/withErrorBoundary';

interface ChangeTransferProps {
  data: DynamicEventResponse;
  onChange: (data: DynamicEventResponse) => void;
}

const ChangeTransfer: FC<ChangeTransferProps> = ({ data, ...props }) => {
  const { t } = useTranslation();
  const { isRecaptchaReady, handleRecapcha } = useRecaptcha();
  const [isLoading, setIsLoading] = useState(false);
  const limit = 3;
  const [forwardDisplayCount, setForwardDisplayCount] = useState(limit);
  const [returnDisplayCount, setReturnDisplayCount] = useState(limit);
  const { numberOfPax } = useContext(DynamicContext);

  useEffect(() => {
    if (
      (data.dynamicTransfer && !data.dynamicTransfer.allForward.length) ||
      !data.dynamicTransfer
    ) {
      (async () => {
        if (data.dynamicFlight && data.dynamicHotel) {
          if (!isRecaptchaReady) return;

          const token = await handleRecapcha();
          if (!token) return;

          const { selectedFlight } = data.dynamicFlight;
          const { selectedHotel } = data.dynamicHotel;

          const newParams: TransferParams = {
            recaptchaToken: token,
            dynamicSearchToken: data.searchToken,
            transferSearchparams: {
              recaptchaToken: token,
              oneWay: false,
              people: numberOfPax,
              countryCode: selectedFlight!.outbound.arrivalAirport.countryCode,
              departureDate: selectedFlight!.outbound.arrivalDateTime,
              returnDate: selectedFlight!.inbound.departureDateTime,
              pickup: {
                code: data.dynamicFlight.selectedFlight!.outbound.arrivalAirport.cityCode,
                desc: data.dynamicFlight.selectedFlight!.outbound.arrivalAirport.cityDesc,
                type: 'Airport',
                latitude: data.dynamicFlight.arrivalAirportCoords.latitude + '',
                longitude: data.dynamicFlight.arrivalAirportCoords.longitude + '',
              },
              dropoff: {
                code: selectedHotel?.addressInfo.city || '',
                desc: selectedHotel?.addressInfo.address || '',
                type: 'Hotel',
                latitude: selectedHotel?.position.latitude + '',
                longitude: selectedHotel?.position.longitude + '',
              },
            },
          };

          const response = await upsellTransfer(newParams);

          if (response && !response.hasErrors) {
            props.onChange({
              ...data,
              dynamicTransfer: response.dynamicTransfer,
            });
          }
        }
      })();
    }
  }, [isRecaptchaReady]); // eslint-disable-line

  const onClickChangeForward = async (id?: string) => {
    setIsLoading(true);
    const transfer = data.dynamicTransfer?.allForward.find((t) => t.priceKey === id)!;

    const token = await handleRecapcha();
    if (!token) return;

    const response = await changeTransfer(
      token,
      data.searchToken,
      id,
      data.dynamicTransfer?.selectedReturn?.priceKey
    );

    if (response && !response.hasErrors && response.transferChanged) {
      props.onChange({
        ...data,
        packageInfo: {
          ...data.packageInfo,
          packageTotalPrice: response.packageTotalPrice,
        },
        dynamicTransfer: {
          ...data.dynamicTransfer!,
          selectedForward: transfer,
        },
      });
    } else {
      console.error(response?.errorMessage || t('error'));
    }
    setIsLoading(false);
  };

  const onClickChangeReturn = async (id?: string) => {
    setIsLoading(true);
    const transfer = data.dynamicTransfer?.allReturn.find((t) => t.priceKey === id)!;

    const token = await handleRecapcha();
    if (!token) return;

    const response = await changeTransfer(
      token,
      data.searchToken,
      data.dynamicTransfer?.selectedForward?.priceKey,
      id
    );
    if (response && !response.hasErrors && response.transferChanged) {
      props.onChange({
        ...data,
        packageInfo: {
          ...data.packageInfo,
          packageTotalPrice: response.packageTotalPrice,
        },
        dynamicTransfer: {
          ...data.dynamicTransfer!,
          selectedReturn: transfer,
        },
      });
    } else {
      console.error(response?.errorMessage || t('error'));
    }
    setIsLoading(false);
  };

  return (
    <Fragment>
      {!!data.dynamicTransfer?.allForward.length && (
        <div className="default-shadow dyn-transfer-card-wr">
          <div className="title">
            {t('transfer.choose_arrival', {
              from: data.dynamicFlight?.selectedFlight!.inbound.arrivalAirport.code,
              to: data.dynamicHotel?.selectedHotel?.item.name,
            })}
          </div>

          {data.dynamicTransfer.allForward.slice(0, forwardDisplayCount).map((item, index) => (
            <TransferCard
              key={index}
              transfer={item}
              onClickChange={onClickChangeForward}
              isLoading={isLoading}
              selectedTransfer={data.dynamicTransfer?.selectedForward}
            />
          ))}
          {data.dynamicTransfer && data.dynamicTransfer.allForward.length > limit && (
            <Fragment>
              {forwardDisplayCount === limit ? (
                <Button
                  className="btn btn-link"
                  onClick={() => setForwardDisplayCount(data.dynamicTransfer?.allForward.length!)}
                  title={t('show_more')}
                />
              ) : (
                <Button
                  className="btn btn-link"
                  onClick={() => setForwardDisplayCount(limit)}
                  title={t('show_less')}
                />
              )}
            </Fragment>
          )}
        </div>
      )}
      {!!data.dynamicTransfer?.allReturn.length && (
        <div className="default-shadow dyn-transfer-card-wr">
          <div className="title">
            {t('transfer.choose_return', {
              from: data.dynamicHotel?.selectedHotel?.item.name,
              to: data.dynamicFlight?.selectedFlight!.outbound.arrivalAirport.code,
            })}
          </div>
          {data.dynamicTransfer?.allReturn.slice(0, returnDisplayCount).map((item, index) => (
            <TransferCard
              key={index}
              transfer={item}
              onClickChange={onClickChangeReturn}
              isLoading={isLoading}
              selectedTransfer={data.dynamicTransfer?.selectedReturn}
            />
          ))}
          {data.dynamicTransfer && data.dynamicTransfer.allReturn.length > limit && (
            <Fragment>
              {returnDisplayCount === limit ? (
                <Button
                  className="btn btn-link"
                  onClick={() => setReturnDisplayCount(data.dynamicTransfer?.allReturn.length!)}
                  title={t('show_more')}
                />
              ) : (
                <Button
                  className="btn btn-link"
                  onClick={() => setReturnDisplayCount(limit)}
                  title={t('show_less')}
                />
              )}
            </Fragment>
          )}
        </div>
      )}
    </Fragment>
  );
};

export default withErrorBoundary(ChangeTransfer, 'ChangeTransfer');
